import manifest from "../../../../config/questionnaire-asset-families.json";
import type { TestedAssetType } from "./index";
import { TESTED_ASSET_TYPE_IDS } from "./registry";

/**
 * The tested-asset family registry (plan Stage 3, S3 choice 4): one row of
 * per-family facts, shared with Python (`core/asset_families.py`). The facts
 * stay per family; they stop being copied. Stage 3 step 1 only loads it; the
 * resolver reads `exposureBound`, and later steps move the copies here.
 */
export type AssetFamilyMaterial = "media" | "copy" | "link";

/** How respondent prose names an asset of the family. */
export type AssetFamilyRespondentProse = "ordinal" | "name" | "messageText";
/** What an answer, choice or row that *is* the asset reads. */
export type AssetFamilyRespondentAnswer = "prose" | "messageText";

export interface AssetFamilyFacts {
  /** A stimulus shown to people (exposure-bound), or a catalog subject. */
  exposureBound: boolean;
  /**
   * How an asset of this family can be supplied: an upload (`media`), pasted
   * copy (`copy`), a link (`link`: a page captured from its address, or a
   * video's link), or several of these.
   */
  materials: ReadonlyArray<AssetFamilyMaterial>;
  /** An upload is enriched at upload time (its page or ad text is extracted). */
  uploadEnrichment: boolean;
  /**
   * How respondent prose (titles, descriptions, lane copy, any mention) names
   * an asset of this family, in every phase: `ordinal` ("Ad 1", worded by the
   * study language's respondent pack, `respondentGuidance.ordinal`), its
   * `name`, or its `messageText`. An exposure-bound asset whose name no
   * respondent may read reads as its ordinal whatever its family
   * (`assetHasOrdinal`).
   */
  respondentProse: AssetFamilyRespondentProse;
  /**
   * What an answer, choice or row that *is* the asset reads: the prose label
   * (`prose`) or its `messageText`. A product's typed price is appended to
   * every answer label. A pain reads its name, the answer a respondent who
   * has it would pick (B3: one text per pain in the survey, the twins' prompts
   * and the report).
   */
  respondentAnswer: AssetFamilyRespondentAnswer;
  /**
   * Today's three noun vocabularies (SEAM-E-09), one per reader, copied here
   * so they stop being copied elsewhere:
   * - `scorecard`: a scorecard title ("Pack scorecard"), `ASSET_FAMILY_NOUNS`;
   * - `singular`/`plural`: a tested set in prose ("two pack designs"),
   *   `_TESTED_SET_NOUNS`;
   * - `limit`: the per-family cap copy ("3 is the most packaging a study can test"),
   *   `tested-asset-caps.ts`.
   */
  nouns: { scorecard: string; singular: string; plural: string; limit: string };
  /** Content fields an asset needs before compile (`requiredFieldsByType`). */
  requiredFields: ReadonlyArray<string>;
  /** `TESTED_ASSET_MAX_BY_TYPE`. */
  maxPerStudy: number;
}

type AssetFamilyManifest = {
  schemaVersion: "questionnaire-asset-families.v1";
  /** How many files (a picture, a video, a document) one asset holds. */
  filesPerAsset: number;
  families: Record<TestedAssetType, AssetFamilyFacts>;
};

const parsed = manifest as unknown as AssetFamilyManifest;
if (
  parsed.schemaVersion !== "questionnaire-asset-families.v1" ||
  // In order: `TESTED_ASSET_TYPE_IDS` lists the families as the registry keys
  // them, as Python's `TestedAssetType` (read from the registry) does.
  JSON.stringify(Object.keys(parsed.families)) !==
    JSON.stringify(TESTED_ASSET_TYPE_IDS)
) {
  throw new TypeError(
    "Questionnaire asset-family registry does not match TestedAssetType.",
  );
}
if (!Number.isInteger(parsed.filesPerAsset) || parsed.filesPerAsset < 1) {
  throw new TypeError(
    "Questionnaire asset-family registry has an invalid filesPerAsset.",
  );
}
const RESPONDENT_PROSE: ReadonlyArray<string> = ["ordinal", "name", "messageText"];
const RESPONDENT_ANSWER: ReadonlyArray<string> = ["prose", "messageText"];
for (const [family, facts] of Object.entries(parsed.families)) {
  if (
    !RESPONDENT_PROSE.includes(facts.respondentProse) ||
    !RESPONDENT_ANSWER.includes(facts.respondentAnswer)
  ) {
    throw new TypeError(
      `Questionnaire asset family ${family} has an invalid respondent label rule.`,
    );
  }
}

export const ASSET_FAMILY_FACTS: Readonly<
  Record<TestedAssetType, AssetFamilyFacts>
> = Object.freeze(parsed.families);

/**
 * How many files one asset holds (FOUNDATIONS §2.10, owner decision B2-3:
 * one picture or video per item): the registry's `filesPerAsset`, as
 * Python's `FILES_PER_ASSET`. A file is a primary media reference; the
 * slices cut from it (`sliceOf`) are its parts. The applier refuses a write
 * over it (`asset_files_over_limit`); the attach card and the asset panel
 * read it; the capability registry's `asset_pictures` meaning renders it.
 */
export const FILES_PER_ASSET: number = parsed.filesPerAsset;

/** An asset's files: its primary media references, never their slices. */
export function assetFiles<Media extends { sliceOf?: unknown }>(
  media: ReadonlyArray<Media> | null | undefined,
): Array<Media> {
  return (media ?? []).filter((ref) => !ref.sliceOf);
}

/**
 * Families whose material can be supplied as text (`copy`), as Python's
 * `COPY_MATERIAL_FAMILIES`: what a model may add from a description, and whose
 * text chat may edit. A family without `copy` is media only (its content comes
 * from an upload; text can rename it).
 */
export const COPY_MATERIAL_FAMILIES: ReadonlySet<TestedAssetType> = new Set(
  (Object.keys(parsed.families) as Array<TestedAssetType>).filter((family) =>
    parsed.families[family].materials.includes("copy"),
  ),
);

/** Families whose uploads are enriched at upload time (`uploadEnrichment`). */
export const UPLOAD_ENRICHMENT_FAMILIES: ReadonlySet<TestedAssetType> = new Set(
  (Object.keys(parsed.families) as Array<TestedAssetType>).filter(
    (family) => parsed.families[family].uploadEnrichment,
  ),
);

/**
 * Families whose assets need a description before compile, the registry's
 * `requiredFields` (as Python's `DESCRIPTION_REQUIRED_FAMILIES`): their text
 * is what respondents read (a concept, a problem statement). Every validator
 * and the builder's asset check read this one set (QA4 AD N4: the builder
 * required a brand's description and validate did not, so every Save failed).
 */
export const DESCRIPTION_REQUIRED_FAMILIES: ReadonlySet<TestedAssetType> =
  new Set(
    (Object.keys(parsed.families) as Array<TestedAssetType>).filter((family) =>
      parsed.families[family].requiredFields.includes("description"),
    ),
  );

/** "Concept", "Problem statement": a family's singular noun, capitalised. */
export function assetFamilyTitleNoun(family: TestedAssetType): string {
  const noun = ASSET_FAMILY_FACTS[family].nouns.singular;
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

/**
 * Families a respondent is shown (`exposureBound`), in registry order, as
 * Python's `EXPOSURE_BOUND_FAMILIES`: what a link or a file handed to the
 * builder can become, or be the picture of.
 */
export const EXPOSURE_BOUND_FAMILIES: ReadonlyArray<TestedAssetType> =
  Object.freeze(
    (Object.keys(parsed.families) as Array<TestedAssetType>).filter(
      (family) => parsed.families[family].exposureBound,
    ),
  );

/**
 * Whether respondents are shown an asset of `family` (`exposureBound`). A
 * pain or a brand is named in questions, answers and rows, never shown, so it
 * is never a stimulus ref and never a sibling beside an exact subject.
 */
export function isShownAssetFamily(family: unknown): boolean {
  return (EXPOSURE_BOUND_FAMILIES as ReadonlyArray<unknown>).includes(family);
}

/**
 * The families the text-material extractor collects (the `extract_material`
 * pending's `assetType`), as Python's `TEXT_EXTRACTION_FAMILIES`: every copy
 * family except a brand, in the order the extraction wire has always listed
 * them. The registry owns the membership; a family added to or dropped from
 * that subset fails here, at load, instead of drifting.
 */
export const TEXT_EXTRACTION_FAMILIES = [
  "message",
  "concept",
  "pain",
  "product",
  "landing_page",
  "ad",
] as const satisfies ReadonlyArray<TestedAssetType>;
export type TextExtractionFamily = (typeof TEXT_EXTRACTION_FAMILIES)[number];
{
  const members = [...COPY_MATERIAL_FAMILIES].filter(
    (family) => family !== "brand",
  );
  if (
    members.length !== TEXT_EXTRACTION_FAMILIES.length ||
    !members.every((family) =>
      (TEXT_EXTRACTION_FAMILIES as ReadonlyArray<string>).includes(family),
    )
  ) {
    throw new TypeError(
      "TEXT_EXTRACTION_FAMILIES does not name the registry's copy families but a brand.",
    );
  }
}

/** The families, in registry order, whose material includes `material`. */
function familiesTaking(
  material: AssetFamilyMaterial,
): ReadonlyArray<TestedAssetType> {
  return Object.freeze(
    (Object.keys(parsed.families) as Array<TestedAssetType>).filter((family) =>
      parsed.families[family].materials.includes(material),
    ),
  );
}

/**
 * Families an asset can be made from a link (`link`): what the builder's link
 * card offers a pasted address as. Every other family is written or uploaded.
 */
export const LINK_MATERIAL_FAMILIES = familiesTaking("link");

/**
 * Families an asset can be made from an uploaded file (`media`): what the
 * builder's card offers a dropped or pasted image or video as. A copy-only
 * family (a brand, a pain, a claim, a concept, a product) is written, never
 * made from a file.
 */
export const MEDIA_MATERIAL_FAMILIES = familiesTaking("media");

/**
 * The files a respondent is shown: a picture (a captured page is one), a
 * video, a PDF. A page link no capture has turned into a picture
 * (`url_snapshot`) shows nothing.
 */
const SHOWN_FILE_KINDS: ReadonlyArray<unknown> = ["image", "video", "document"];

/**
 * Whether respondents are shown a file of this asset: it holds a file of a
 * kind respondents are shown, whatever its family. An ad, a landing page or a
 * pack is its file, with the box beside it (its name and any caption the user
 * wrote); a copy family's text is its stimulus (a concept's copy, a claim, a
 * product option), shown with any picture attached to it, as every asset's
 * image reaches respondents (`getPrimaryConceptImage`). The reply's facts read
 * it (`turn-reply.ts`; gate 2026-10-16, PR-14: a product's photo, on screen
 * for every question, was said as no photo, from a filter on the media
 * families).
 */
export function assetFileShown(asset: { media?: unknown }): boolean {
  const media = Array.isArray(asset.media)
    ? (asset.media.filter(
        (ref) => ref && typeof ref === "object",
      ) as Array<{ kind?: unknown; sliceOf?: unknown }>)
    : [];
  return assetFiles(media).some((ref) => SHOWN_FILE_KINDS.includes(ref.kind));
}

/**
 * Per-type roster ceilings, the registry's `maxPerStudy` (plan Stage 3, S3.4
 * row 24): one table for every write path. Python reads the same registry
 * (`core/asset_families.py`) for PLAN and revise.
 */
export const TESTED_ASSET_MAX_BY_TYPE: Readonly<
  Record<TestedAssetType, number>
> = Object.freeze(
  Object.fromEntries(
    Object.entries(ASSET_FAMILY_FACTS).map(([family, facts]) => [
      family,
      facts.maxPerStudy,
    ]),
  ) as Record<TestedAssetType, number>,
);

export function isAssetFamily(value: unknown): value is TestedAssetType {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(ASSET_FAMILY_FACTS, value)
  );
}
